import { supabase } from '../config/supabase.js';
import { FaxingService } from './faxing-service.js';
export class ClinicalNotesService {
    // Clinical Notes CRUD operations
    static async createClinicalNote(data) {
        console.log('🚨🚨🚨 COMPLETELY NEW VERSION:', Date.now(), 'CACHE BUST TEST');
        console.log('🚨🚨🚨 FORCE BROWSER RELOAD: CREATE CLINICAL NOTE called at', new Date().toISOString());
        console.log('🔍 CREATE CLINICAL NOTE: Input data received:', JSON.stringify(data, null, 2));
        // Combine clinical note fields for the required content column
        const clinicalNoteText = data.clinical_note || '';
        const internalNoteText = data.internal_note || '';
        const visitSummaryText = data.visit_summary || '';
        const combinedContent = [
            clinicalNoteText,
            internalNoteText,
            visitSummaryText
        ].filter(text => text.trim()).join('\n\n');
        console.log('🔍 CREATE CLINICAL NOTE: Combined content:', combinedContent);
        console.log('🔍 CREATE CLINICAL NOTE: Content length:', combinedContent.length);
        const insertPayload = {
            ...data,
            allergies: data.allergies || [],
            previous_medications: data.previous_medications || [],
            current_medications: data.current_medications || [],
            clinical_note: clinicalNoteText,
            internal_note: internalNoteText,
            visit_summary: visitSummaryText,
            content: combinedContent || 'Clinical note created', // Required field
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
        };
        console.log('🔍 CREATE CLINICAL NOTE: Insert payload:', JSON.stringify(insertPayload, null, 2));
        const { data: clinicalNote, error } = await supabase
            .from('clinical_notes')
            .insert(insertPayload)
            .select()
            .single();
        console.log('🔍 CREATE CLINICAL NOTE: Supabase response - error:', error);
        console.log('🔍 CREATE CLINICAL NOTE: Supabase response - data:', clinicalNote);
        if (error) {
            console.error('❌ CREATE CLINICAL NOTE: Error details:', {
                message: error.message,
                details: error.details,
                hint: error.hint,
                code: error.code
            });
            throw new Error(`Failed to create clinical note: ${error.message}`);
        }
        return clinicalNote;
    }
    static async getClinicalNoteByAppointmentId(appointmentId) {
        console.log('🔍 GET CLINICAL NOTE BY APPOINTMENT: Looking for appointmentId:', appointmentId);
        // Use service client to bypass RLS policies, like other working parts of the codebase
        const { createClient } = await import('@supabase/supabase-js');
        const { getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } = await import('../config/supabase.js');
        const baseUrl = getEnvironmentAwareSupabaseUrl();
        const serviceKey = getEnvironmentAwareSupabaseServiceKey();
        const serviceClient = createClient(baseUrl, serviceKey, {
            auth: {
                autoRefreshToken: false,
                persistSession: false,
                detectSessionInUrl: false
            }
        });
        console.log('🔍 GET CLINICAL NOTE BY APPOINTMENT: Using service client to bypass RLS');
        const { data: clinicalNote, error } = await serviceClient
            .from('clinical_notes')
            .select('*')
            .eq('appointment_id', appointmentId)
            .single();
        console.log('🔍 GET CLINICAL NOTE BY APPOINTMENT: Service client response - error:', error);
        console.log('🔍 GET CLINICAL NOTE BY APPOINTMENT: Service client response - data:', clinicalNote);
        if (error) {
            if (error.code === 'PGRST116') {
                console.log('🔍 GET CLINICAL NOTE BY APPOINTMENT: No existing note found (PGRST116)');
                // No rows returned
                return null;
            }
            console.error('❌ GET CLINICAL NOTE BY APPOINTMENT: Error:', error);
            throw new Error(`Failed to get clinical note: ${error.message}`);
        }
        console.log('🔍 GET CLINICAL NOTE BY APPOINTMENT: Found existing note with ID:', clinicalNote?.id);
        return clinicalNote;
    }
    static async getClinicalNoteById(id) {
        const { data: clinicalNote, error } = await supabase
            .from('clinical_notes')
            .select('*')
            .eq('id', id)
            .single();
        if (error) {
            if (error.code === 'PGRST116') {
                // No rows returned
                return null;
            }
            throw new Error(`Failed to get clinical note: ${error.message}`);
        }
        return clinicalNote;
    }
    static async updateClinicalNote(id, updates) {
        // Combine clinical note fields for the required content column
        const clinicalNoteText = updates.clinical_note || '';
        const internalNoteText = updates.internal_note || '';
        const visitSummaryText = updates.visit_summary || '';
        const combinedContent = [
            clinicalNoteText,
            internalNoteText,
            visitSummaryText
        ].filter(text => text.trim()).join('\n\n');
        const { data: clinicalNote, error } = await supabase
            .from('clinical_notes')
            .update({
            ...updates,
            content: combinedContent || 'Clinical note updated', // Required field
            updated_at: new Date().toISOString()
        })
            .eq('id', id)
            .select()
            .single();
        if (error) {
            throw new Error(`Failed to update clinical note: ${error.message}`);
        }
        return clinicalNote;
    }
    static async upsertClinicalNote(appointmentId, data) {
        console.log('🔍 UPSERT CLINICAL NOTE: Starting with appointmentId:', appointmentId);
        console.log('🔍 UPSERT CLINICAL NOTE: Input data:', JSON.stringify(data, null, 2));
        // First, try to get existing clinical note for this appointment
        const existingNote = await this.getClinicalNoteByAppointmentId(appointmentId);
        console.log('🔍 UPSERT CLINICAL NOTE: Existing note found:', !!existingNote);
        if (existingNote) {
            console.log('🔍 UPSERT CLINICAL NOTE: Updating existing note with ID:', existingNote.id);
            // Update existing clinical note
            return await this.updateClinicalNote(existingNote.id, data);
        }
        else {
            console.log('🔍 UPSERT CLINICAL NOTE: Creating new note');
            // Ensure appointment_id is set for new clinical notes
            const createData = {
                ...data,
                appointment_id: appointmentId // Explicitly set appointment_id
            };
            console.log('🔍 UPSERT CLINICAL NOTE: Create data with appointment_id:', JSON.stringify(createData, null, 2));
            // Create new clinical note
            return await this.createClinicalNote(createData);
        }
    }
    static async getClinicalNotesByPatientId(patientId) {
        const { data: clinicalNotes, error } = await supabase
            .from('clinical_notes')
            .select('*')
            .eq('patient_id', patientId)
            .order('created_at', { ascending: false });
        if (error) {
            throw new Error(`Failed to get clinical notes: ${error.message}`);
        }
        return clinicalNotes || [];
    }
    static async getClinicalNotesByProviderId(providerId) {
        const { data: clinicalNotes, error } = await supabase
            .from('clinical_notes')
            .select('*')
            .eq('provider_id', providerId)
            .order('created_at', { ascending: false });
        if (error) {
            throw new Error(`Failed to get clinical notes: ${error.message}`);
        }
        return clinicalNotes || [];
    }
    static async deleteClinicalNote(id) {
        const { error } = await supabase
            .from('clinical_notes')
            .delete()
            .eq('id', id);
        if (error) {
            throw new Error(`Failed to delete clinical note: ${error.message}`);
        }
    }
    // Visit Interactions CRUD operations
    static async createVisitInteraction(data) {
        const { data: interaction, error } = await supabase
            .from('visit_interactions')
            .insert({
            ...data,
            created_at: new Date().toISOString()
        })
            .select()
            .single();
        if (error) {
            throw new Error(`Failed to create visit interaction: ${error.message}`);
        }
        return interaction;
    }
    static async getVisitInteractionsByAppointmentId(appointmentId) {
        const { data: interactions, error } = await supabase
            .from('visit_interactions')
            .select('*')
            .eq('appointment_id', appointmentId)
            .order('created_at', { ascending: false });
        if (error) {
            throw new Error(`Failed to get visit interactions: ${error.message}`);
        }
        return interactions || [];
    }
    static async getVisitInteractionsByClinicalNoteId(clinicalNoteId) {
        console.log('🔍 GET VISIT INTERACTIONS: Looking for clinicalNoteId:', clinicalNoteId);
        // Use service client to bypass RLS policies, like other working parts of the codebase
        const { createClient } = await import('@supabase/supabase-js');
        const { getEnvironmentAwareSupabaseUrl, getEnvironmentAwareSupabaseServiceKey } = await import('../config/supabase.js');
        const baseUrl = getEnvironmentAwareSupabaseUrl();
        const serviceKey = getEnvironmentAwareSupabaseServiceKey();
        const serviceClient = createClient(baseUrl, serviceKey, {
            auth: {
                autoRefreshToken: false,
                persistSession: false,
                detectSessionInUrl: false
            }
        });
        console.log('🔍 GET VISIT INTERACTIONS: Using service client to bypass RLS');
        const { data: interactions, error } = await serviceClient
            .from('visit_interactions')
            .select('*')
            .eq('clinical_note_id', clinicalNoteId)
            .order('created_at', { ascending: false });
        console.log('🔍 GET VISIT INTERACTIONS: Service client response - error:', error);
        console.log('🔍 GET VISIT INTERACTIONS: Service client response - data:', interactions);
        if (error) {
            console.error('❌ GET VISIT INTERACTIONS: Error:', error);
            throw new Error(`Failed to get visit interactions: ${error.message}`);
        }
        return interactions || [];
    }
    static async getMedicationInteractionsByAppointmentId(appointmentId) {
        const { data: interactions, error } = await supabase
            .from('visit_interactions')
            .select('*')
            .eq('appointment_id', appointmentId)
            .eq('interaction_type', 'medication_adjustment')
            .order('created_at', { ascending: false });
        if (error) {
            throw new Error(`Failed to get medication interactions: ${error.message}`);
        }
        return interactions || [];
    }
    // Visit Medication Adjustments CRUD operations
    static async createVisitMedicationAdjustment(data) {
        const { data: adjustment, error } = await supabase
            .from('visit_medication_adjustments')
            .insert({
            ...data,
            created_at: new Date().toISOString()
        })
            .select()
            .single();
        if (error) {
            throw new Error(`Failed to create visit medication adjustment: ${error.message}`);
        }
        return adjustment;
    }
    static async getVisitMedicationAdjustmentsByAppointmentId(appointmentId) {
        const { data: adjustments, error } = await supabase
            .from('visit_medication_adjustments')
            .select('*')
            .eq('appointment_id', appointmentId)
            .order('created_at', { ascending: false });
        if (error) {
            throw new Error(`Failed to get visit medication adjustments: ${error.message}`);
        }
        return adjustments || [];
    }
    static async getVisitMedicationAdjustmentsByClinicalNoteId(clinicalNoteId) {
        const { data: adjustments, error } = await supabase
            .from('visit_medication_adjustments')
            .select('*')
            .eq('clinical_note_id', clinicalNoteId)
            .order('created_at', { ascending: false });
        if (error) {
            throw new Error(`Failed to get visit medication adjustments: ${error.message}`);
        }
        return adjustments || [];
    }
    static async getPatientPrescriptionHistory(patientId) {
        // Get prescription history for a patient by joining visit_medication_adjustments 
        // with preferences, medications, appointments, and provider info
        console.log('🔍 PRESCRIPTION HISTORY SERVICE: Starting query for patient ID:', patientId);
        // First check if this is a profile_id vs patient id by trying to find patient record
        let actualPatientId = patientId;
        // If the ID looks like a UUID profile ID, try to convert it to database patient ID  
        if (patientId && patientId.includes('-')) {
            console.log('🔍 PRESCRIPTION HISTORY SERVICE: ID looks like profile ID, checking if we need to convert...');
            const { data: patientRecord, error: patientError } = await supabase
                .from('patients')
                .select('id, profile_id')
                .eq('profile_id', patientId)
                .maybeSingle();
            if (patientRecord && !patientError) {
                actualPatientId = patientRecord.id;
                console.log('✅ PRESCRIPTION HISTORY SERVICE: Converted profile_id to patient_id:', patientId, '->', actualPatientId);
            }
            else {
                console.log('🔍 PRESCRIPTION HISTORY SERVICE: Using provided ID as-is (might already be patient_id):', patientId);
            }
        }
        // First get all patient medication preferences for this patient to get their IDs
        const { data: patientPreferences, error: preferencesError } = await supabase
            .from('patient_medication_preferences')
            .select('id')
            .eq('patient_id', actualPatientId);
        if (preferencesError) {
            throw new Error(`Failed to get patient preferences: ${preferencesError.message}`);
        }
        if (!patientPreferences || patientPreferences.length === 0) {
            console.log('🔍 PRESCRIPTION HISTORY SERVICE: No preferences found for patient, returning empty history');
            return [];
        }
        const preferenceIds = patientPreferences.map(p => p.id);
        console.log('🔍 PRESCRIPTION HISTORY SERVICE: Found preference IDs:', preferenceIds);
        // Now get visit medication adjustments for these preferences only
        const { data: history, error } = await supabase
            .from('visit_medication_adjustments')
            .select(`
        *,
        patient_medication_preferences!preference_id(
          patient_id
        ),
        medications!medication_id(
          name,
          brand_name,
          category
        ),
        appointments!appointment_id(
          appointment_date,
          provider_id,
          providers(
            profile_id,
            profiles(
              first_name,
              last_name
            )
          )
        )
      `)
            .in('preference_id', preferenceIds)
            .order('created_at', { ascending: false });
        if (error) {
            throw new Error(`Failed to get patient prescription history: ${error.message}`);
        }
        console.log('🔍 PRESCRIPTION HISTORY SERVICE: Raw database response:', JSON.stringify(history, null, 2));
        // Transform the data into the PrescriptionHistoryItem format
        const prescriptionHistory = (history || []).map((adjustment, index) => {
            const preference = adjustment.patient_medication_preferences;
            const medication = adjustment.medications; // Now using medication directly from adjustments table
            const appointment = adjustment.appointments;
            const provider = appointment?.providers?.profiles;
            console.log(`🔍 PRESCRIPTION HISTORY SERVICE: Processing adjustment ${index + 1}:`, {
                adjustment_id: adjustment.id,
                adjustment_medication_id: adjustment.medication_id,
                medication: medication,
                previous_dosage: adjustment.previous_dosage,
                new_dosage: adjustment.new_dosage,
                medication_name: medication?.name || 'Unknown Medication'
            });
            return {
                adjustment_date: adjustment.created_at,
                medication_name: medication?.name || 'Unknown Medication',
                medication_brand: medication?.brand_name,
                medication_category: medication?.category || '',
                previous_dosage: adjustment.previous_dosage,
                new_dosage: adjustment.new_dosage,
                previous_frequency: adjustment.previous_frequency,
                new_frequency: adjustment.new_frequency,
                previous_status: adjustment.previous_status,
                new_status: adjustment.new_status,
                adjustment_reason: adjustment.adjustment_reason,
                provider_notes: adjustment.provider_notes,
                appointment_date: appointment?.appointment_date || '',
                provider_name: provider ? `${provider.first_name} ${provider.last_name}`.trim() : ''
            };
        });
        return prescriptionHistory;
    }
    // Helper function to capture medication state before changes
    static async captureMedicationState(preferenceId) {
        try {
            const { data: preference, error } = await supabase
                .from('patient_medication_preferences')
                .select('preferred_dosage, frequency, status, notes, supply_days')
                .eq('id', preferenceId)
                .single();
            if (error) {
                console.error('Failed to capture medication state:', error);
                return null;
            }
            return {
                dosage: preference.preferred_dosage,
                frequency: preference.frequency,
                status: preference.status,
                provider_notes: preference.notes,
                supply_days: preference.supply_days
            };
        }
        catch (error) {
            console.error('Exception capturing medication state:', error);
            return null;
        }
    }
    // Enhanced medication adjustment function that creates historical record
    static async recordMedicationAdjustment(clinicalNoteId, appointmentId, preferenceId, medicationId, previousState, newState, adjustmentData) {
        const adjustmentRecord = {
            clinical_note_id: clinicalNoteId,
            appointment_id: appointmentId,
            preference_id: preferenceId,
            medication_id: medicationId,
            previous_dosage: previousState.dosage,
            previous_frequency: previousState.frequency,
            previous_status: previousState.status,
            previous_provider_notes: previousState.provider_notes,
            new_dosage: newState.dosage || previousState.dosage,
            new_frequency: newState.frequency || previousState.frequency,
            new_status: newState.status || previousState.status,
            new_provider_notes: newState.provider_notes || previousState.provider_notes,
            new_supply_days: newState.supply_days || previousState.supply_days,
            adjustment_reason: adjustmentData.adjustment_reason,
            provider_notes: adjustmentData.provider_notes,
            performed_by: adjustmentData.performed_by
        };
        return await this.createVisitMedicationAdjustment(adjustmentRecord);
    }
    // Combined operations for convenience
    static async saveClinicalNoteWithInteractions(appointmentId, clinicalNoteData, interactions = []) {
        // Save clinical note
        const clinicalNote = await this.upsertClinicalNote(appointmentId, clinicalNoteData);
        // Save interactions
        const savedInteractions = [];
        for (const interactionData of interactions) {
            const interaction = await this.createVisitInteraction({
                ...interactionData,
                clinical_note_id: clinicalNote.id,
                appointment_id: appointmentId
            });
            savedInteractions.push(interaction);
        }
        // Note: Medication adjustment notifications are now handled by database triggers
        // when patient_medication_preferences table is updated during clinical note processing
        return { clinicalNote, interactions: savedInteractions };
    }
    /**
     * Complete clinical note and automatically trigger prescription faxing
     * This is the main workflow method that providers will use after finishing patient visits
     */
    static async completeClinicalNoteWithPrescription(appointmentId, clinicalNoteData, prescriptionOptions, interactions = []) {
        console.log('🔍 CLINICAL NOTES: Completing clinical note with prescription for appointment:', appointmentId);
        console.log('🔍 CLINICAL NOTES: Prescription options:', prescriptionOptions);
        try {
            // First, save the clinical note and interactions
            const { clinicalNote, interactions: savedInteractions } = await this.saveClinicalNoteWithInteractions(appointmentId, clinicalNoteData, interactions);
            console.log('✅ CLINICAL NOTES: Clinical note saved successfully:', clinicalNote.id);
            // Check if we should automatically send prescription
            const shouldSendPrescription = prescriptionOptions.autoSendPrescription !== false;
            if (shouldSendPrescription) {
                console.log('🔍 CLINICAL NOTES: Auto-sending prescription to pharmacy...');
                // Generate and send prescription fax - this will trigger order creation
                const faxResult = await FaxingService.generatePrescriptionFromClinicalNote(clinicalNote.id, appointmentId, prescriptionOptions.pharmacyFaxNumber, prescriptionOptions.pharmacyName);
                if (faxResult.success && faxResult.data) {
                    console.log('✅ CLINICAL NOTES: Prescription fax sent successfully');
                    console.log('🤖 CLINICAL NOTES: Medication order should be created automatically via database trigger');
                    return {
                        clinicalNote,
                        interactions: savedInteractions,
                        prescriptionFax: faxResult.data.fax,
                        orderCreated: faxResult.data.triggeredOrderCreation
                    };
                }
                else {
                    console.warn('⚠️ CLINICAL NOTES: Failed to send prescription fax:', faxResult.error);
                    return {
                        clinicalNote,
                        interactions: savedInteractions,
                        error: `Clinical note saved but prescription fax failed: ${faxResult.error}`
                    };
                }
            }
            else {
                console.log('🔍 CLINICAL NOTES: Prescription auto-send disabled, clinical note saved only');
                return {
                    clinicalNote,
                    interactions: savedInteractions
                };
            }
        }
        catch (error) {
            console.error('❌ CLINICAL NOTES: Error completing clinical note with prescription:', error);
            throw new Error(`Failed to complete clinical note: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Send prescription for an existing clinical note
     * Use this when prescription wasn't sent during initial clinical note creation
     */
    static async sendPrescriptionForClinicalNote(clinicalNoteId, appointmentId, pharmacyFaxNumber, pharmacyName) {
        console.log('🔍 CLINICAL NOTES: Sending prescription for existing clinical note:', clinicalNoteId);
        return await FaxingService.generatePrescriptionFromClinicalNote(clinicalNoteId, appointmentId, pharmacyFaxNumber, pharmacyName);
    }
    static async getClinicalNoteWithInteractions(appointmentId) {
        const clinicalNote = await this.getClinicalNoteByAppointmentId(appointmentId);
        const interactions = await this.getVisitInteractionsByAppointmentId(appointmentId);
        return { clinicalNote, interactions };
    }
}
